import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import { getCurrencyOverride } from "@/components/CurrencySelector";

interface ExchangeRates {
  EUR: number;
  PLN: number;
  GBP: number;
  USD: number;
  CHF: number;
}

// Cache exchange rates globally to avoid refetching
let cachedRates: ExchangeRates | null = null;
let lastFetchTime: number = 0;
const CACHE_DURATION = 3600000; // 1 hour

// Sensible fallback rates relative to EUR base
const FALLBACK_RATES: ExchangeRates = {
  EUR: 1,
  PLN: 4.3,
  GBP: 0.85,
  USD: 1.08,
  CHF: 0.95,
};

export const useCurrencyFormat = () => {
  const { t, i18n } = useTranslation();
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>(
    cachedRates || FALLBACK_RATES
  );
  const [isLoading, setIsLoading] = useState(!cachedRates);
  const [currencyOverride, setCurrencyOverride] = useState<string | null>(
    getCurrencyOverride(i18n.language)
  );

  useEffect(() => {
    const handler = () => {
      setCurrencyOverride(getCurrencyOverride(i18n.language));
    };
    window.addEventListener("currency-change", handler);
    return () => window.removeEventListener("currency-change", handler);
  }, [i18n.language]);

  useEffect(() => {
    setCurrencyOverride(getCurrencyOverride(i18n.language));
  }, [i18n.language]);

  useEffect(() => {
    const fetchRates = async () => {
      if (cachedRates && Date.now() - lastFetchTime < CACHE_DURATION) {
        setExchangeRates(cachedRates);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          "https://api.exchangerate-api.com/v4/latest/EUR"
        );
        if (!response.ok) throw new Error("Failed to fetch exchange rates");
        const data = await response.json();
        const rates: ExchangeRates = {
          EUR: 1,
          PLN: data.rates.PLN || FALLBACK_RATES.PLN,
          GBP: data.rates.GBP || FALLBACK_RATES.GBP,
          USD: data.rates.USD || FALLBACK_RATES.USD,
          CHF: data.rates.CHF || FALLBACK_RATES.CHF,
        };
        cachedRates = rates;
        lastFetchTime = Date.now();
        setExchangeRates(rates);
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Defer the network request out of the critical path — fallback rates
    // render immediately, and live rates hydrate once the browser is idle.
    // This removes exchangerate-api.com from the LCP dependency tree.
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(() => { void fetchRates(); }, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(() => { void fetchRates(); }, 1500);
    }
    return () => {
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  const getCurrencyCode = useCallback((): string => {
    if (currencyOverride) return currencyOverride;
    return t("currency.code") || "EUR";
  }, [t, currencyOverride]);

  const getCurrencySymbol = useCallback((): string => {
    const code = getCurrencyCode();
    switch (code) {
      case "USD": return "$";
      case "GBP": return "£";
      case "CHF": return "CHF";
      case "PLN": return "zł";
      case "EUR":
      default: return "€";
    }
  }, [getCurrencyCode]);

  const getExchangeRate = useCallback((): number => {
    const code = getCurrencyCode();
    return exchangeRates[code as keyof ExchangeRates] ?? 1;
  }, [getCurrencyCode, exchangeRates]);

  // NOTE: All hardcoded component prices are now treated as EUR (base currency).
  const formatPrice = useCallback((eurAmount: number | string): string => {
    const symbol = getCurrencySymbol();
    const rate = getExchangeRate();

    let numericAmount: number;
    if (typeof eurAmount === "string") {
      const cleaned = eurAmount.replace(/[£€$zł,\s]|CHF/gi, "");
      numericAmount = parseFloat(cleaned);
    } else {
      numericAmount = eurAmount;
    }

    if (isNaN(numericAmount)) return `${symbol}0`;

    const converted = numericAmount * rate;
    const code = getCurrencyCode();
    const rounded = Math.round(converted);

    const locale =
      code === "PLN" ? "pl-PL" :
      code === "EUR" ? "de-DE" :
      code === "CHF" ? "de-CH" :
      "en-GB";
    const formatted = rounded.toLocaleString(locale);

    // Symbol placement: after for EUR / PLN / CHF; before for GBP / USD
    if (code === "PLN" || code === "EUR") return `${formatted} ${symbol}`;
    if (code === "CHF") return `${symbol} ${formatted}`;
    return `${symbol}${formatted}`;
  }, [getCurrencySymbol, getExchangeRate, getCurrencyCode]);

  const formatPricePerUnit = useCallback((eurAmount: number | string, unitKey: string): string => {
    const price = formatPrice(eurAmount);
    const unit = t(unitKey) || unitKey;
    return `${price}${unit}`;
  }, [formatPrice, t]);

  return {
    formatPrice,
    formatPricePerUnit,
    getCurrencySymbol,
    getCurrencyCode,
    getExchangeRate,
    exchangeRates,
    isLoading,
  };
};
